import React from "react";
import { useLocation } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { clearStoredAccessToken, hasStoredAccessToken } from "@/lib/authToken";

const CONSENT_KEY = "marstick_cookie_consent_v2";
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "G-ML5WLWTQXC";

function getAnalyticsConsent() {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) return false;
    if (saved === "accepted") return true;
    if (saved === "rejected") return false;
    return JSON.parse(saved)?.analytics === true;
  } catch {
    return false;
  }
}

function getSailingSection(pathname) {
  const cleanPath = pathname.replace(/^\/(en|he|es|it|fr|de|pt|ar|el|ru|tr|zh)(?=\/|$)/, "") || "/";
  if (cleanPath.startsWith("/learning-center")) return "learning_guide";
  if (cleanPath.startsWith("/weather")) return "weather_guide";
  if (cleanPath.startsWith("/marinas")) return "marina_route";
  if (cleanPath.startsWith("/routes")) return "sailing_route";
  if (cleanPath.startsWith("/regulations")) return "regulation_guide";
  if (cleanPath.startsWith("/glossary")) return "glossary_guide";
  if (cleanPath.startsWith("/sailing-calculators")) return "calculator_guide";
  if (cleanPath.startsWith("/safety")) return "safety_guide";
  return "public_sailing_page";
}

function ensureGoogleAnalytics() {
  if (typeof window === "undefined") return false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  if (!document.getElementById("marstick-google-analytics")) {
    const script = document.createElement("script");
    script.id = "marstick-google-analytics";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  }
  return true;
}

export default function GoogleAnalyticsRouteTracker() {
  const location = useLocation();
  const [analyticsAllowed, setAnalyticsAllowed] = React.useState(() => getAnalyticsConsent());
  const sentScrollMilestones = React.useRef(new Set());

  React.useEffect(() => {
    const syncConsent = () => setAnalyticsAllowed(getAnalyticsConsent());
    window.addEventListener("marstick:consent-updated", syncConsent);
    return () => window.removeEventListener("marstick:consent-updated", syncConsent);
  }, []);

  React.useEffect(() => {
    sentScrollMilestones.current.clear();
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (!analyticsAllowed || !ensureGoogleAnalytics()) return;
    const pagePath = `${location.pathname}${location.search || ""}`;
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
      sailing_section: getSailingSection(location.pathname),
    });
  }, [analyticsAllowed, location.pathname, location.search]);

  React.useEffect(() => {
    if (!analyticsAllowed || !ensureGoogleAnalytics()) return;

    if (!hasStoredAccessToken()) return;

    base44.auth.isAuthenticated().then(async (isAuthenticated) => {
      if (!isAuthenticated) return;
      const user = await base44.auth.me();
      if (!user?.id) return;

      const markerKey = `marstick_ga_registration_tracked_${user.id}`;
      if (localStorage.getItem(markerKey) === "true") return;

      const createdAt = user.created_date ? new Date(user.created_date).getTime() : 0;
      const isRecentRegistration = createdAt && Date.now() - createdAt < 24 * 60 * 60 * 1000;
      if (!isRecentRegistration) return;

      window.gtag("event", "sign_up", {
        method: "base44",
        page_path: `${location.pathname}${location.search || ""}`,
      });
      localStorage.setItem(markerKey, "true");
    }).catch(() => clearStoredAccessToken());
  }, [analyticsAllowed, location.pathname, location.search]);

  React.useEffect(() => {
    if (!analyticsAllowed || !ensureGoogleAnalytics()) return undefined;

    const pagePath = `${location.pathname}${location.search || ""}`;
    const sailingSection = getSailingSection(location.pathname);

    const engagedTimer = window.setTimeout(() => {
      window.gtag("event", "engaged_visit", {
        page_path: pagePath,
        sailing_section: sailingSection,
        engagement_time_msec: 30000,
      });
    }, 30000);

    const trackScrollDepth = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const depth = Math.round((window.scrollY / scrollable) * 100);
      [25, 50, 75, 90].forEach((milestone) => {
        if (depth >= milestone && !sentScrollMilestones.current.has(milestone)) {
          sentScrollMilestones.current.add(milestone);
          window.gtag("event", "scroll_depth", {
            page_path: pagePath,
            percent_scrolled: milestone,
            sailing_section: sailingSection,
          });
        }
      });
    };

    const trackLinkClick = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (/^https?:\/\//.test(href) && !href.includes(window.location.host)) {
        window.gtag("event", "outbound_click", {
          link_url: href,
          page_path: pagePath,
          sailing_section: sailingSection,
        });
      }
    };

    window.addEventListener("scroll", trackScrollDepth, { passive: true });
    document.addEventListener("click", trackLinkClick);
    trackScrollDepth();

    return () => {
      window.clearTimeout(engagedTimer);
      window.removeEventListener("scroll", trackScrollDepth);
      document.removeEventListener("click", trackLinkClick);
    };
  }, [analyticsAllowed, location.pathname, location.search]);

  return null;
}